import React, { useState, useEffect, useMemo } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrDestination } from "Api/destinationsAPI";
import { consoleLog } from "helpers";

const CreateOrUpdateDestinationModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
}) => {
  const {
    destinationFormData,
    setDestinationFormData,
    initialDestinationFormState,
    setFetchDestinations,
    setSearchQuery,
  } = useGlobalContext();

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [hasChanged, setHasChanged] = useState(false);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setDestinationFormData({
      ...destinationFormData,
      [name]: value,
    });
    setHasChanged(true);
    if (name === "key") {
      setErrorMessage("");
    }
  };

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }
    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");

    try {
      const response = await createOrDestination(
        destinationFormData,
        setIsSubmitting,
        token
      );
      consoleLog(["API Response:", response]);

      if (response?.id) {
        const successMessage = destinationFormData?.id
          ? "Destination updated successfully"
          : "Destination created successfully";
        toast.success(successMessage);

        onSubmit(response);
        onClose();
        setSearchQuery("");
        setDestinationFormData(initialDestinationFormState);
        setFetchDestinations(true);

        setSearchQuery("");
      } else {
        console.error("Error: Invalid response data", response);
        toast.error("Error creating/updating Destination. Please try again.");
      }
    } catch (error) {
      toast.error("An unexpected error occurred. Please try again.");
      console.error("API Error:", error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    setDestinationFormData(initialDestinationFormState);
    setHasChanged(false);
    onClose();
  };

  const isFormValid = useMemo(() => {
    return (
      destinationFormData?.Name &&
      destinationFormData?.Type &&
      destinationFormData?.Title &&
      destinationFormData?.Subtitle &&
      destinationFormData?.Region &&
      !errorMessage
    );
  }, [destinationFormData, errorMessage]);

  if (!isOpen) return null;

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center bg-opacity-50">
      <div className="relative z-50 mx-auto flex w-4/5 flex-col items-center justify-start rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 ">
         <button
                                         type="button"
                                         onClick={handleCancel}
                                          className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
                                       >
                                         <IoMdClose size={24} />
                                       </button>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Name:
              </label>
              <input
                type="text"
                name="Name"
                autoComplete="off"
                value={destinationFormData?.Name}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
              />
            </div>
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700  dark:text-white">
                <span className="text-red-600">*</span> Type:
              </label>
              <input
                type="text"
                name="Type"
                autoComplete="off"
                value={destinationFormData?.Type}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white"
              />
            </div>
          </div>
          <div className="mt-4 mb-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700  dark:text-white">
                <span className="text-red-600">*</span> Title:
              </label>
              <input
                type="text"
                name="Title"
                autoComplete="off"
                value={destinationFormData?.Title}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white"
              />
            </div>
            <div className="w-1/2">
              <label className="mb-2 block font-bold text-gray-700 dark:text-white">
                <span className="text-red-600">*</span> Region:
              </label>
              <input
                type="text"
                name="Region"
                autoComplete="off"
                value={destinationFormData?.Region}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white"
                required
              />
            </div>
          </div>
          <div className="mb-4 w-full">
            <label className="mb-2 block font-bold text-gray-700 dark:text-white">
              <span className="text-red-600">*</span> Subtitle:
            </label>
            <textarea
              name="Subtitle"
              value={destinationFormData?.Subtitle}
              onChange={handleChange}
              className="focus:border-transparent min-h-[150px] w-full resize-y rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500  dark:!bg-gray-10 dark:text-white"
              required
            ></textarea>
          </div>
          <div className="mb-8 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${
                isSubmitting || !isFormValid || !hasChanged
                  ? "cursor-not-allowed bg-gray-400"
                  : "bg-green-600 hover:bg-green-500"
              }`}
              disabled={isSubmitting || !isFormValid || !hasChanged}
            >
              {isSubmitting ? "Submitting..." : isEditMode ? "Update" : "Add"}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CreateOrUpdateDestinationModal;
